import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import FileUploader from 'UI/components/molecules/FileUploader';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { FeeAgreementFileCategoryId } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { REQUIRED_VALIDATION } from 'UI/utils';

import { FileKeyPrefix } from '../utils';

/**
 * @typedef {Object} PlacementFilesSectionProps
 * @property {boolean} [readOnly]
 * @property {*[]} fileCategories
 * @property {*[]} [files]
 * @property {FileExplorerSection[]} [fileExplorerSections]
 * @property {boolean} [softDeleteForExistingItems]
 * @property {*} [agreement]
 */

const maxNumberOfFiles = 10;

/**
 * @param {PlacementFilesSectionProps} props
 */
const PlacementFilesSection = props => {
  const {
    readOnly,
    fileCategories,
    files,
    fileExplorerSections,
    softDeleteForExistingItems,
    agreement
  } = props;
  const { register, unregister, errors, setValue, clearError } = useFormContext();

  useEffect(() => {
    fileCategories.forEach(({ id, required }) =>
      required
        ? register(
            { name: `${FileKeyPrefix}${id}` },
            {
              validate(selectedFiles) {
                return selectedFiles?.length > 0 || REQUIRED_VALIDATION.required;
              }
            }
          )
        : register({ name: `${FileKeyPrefix}${id}` })
    );
    return () =>
      fileCategories.forEach(({ id }) => {
        unregister(`${FileKeyPrefix}${id}`);
        unregister(`${FileKeyPrefix}${id}`);
      });
  }, [register, unregister, fileCategories]);

  useEffect(() => {
    agreement && setValue(`${FileKeyPrefix}${agreement.file_type_id}`, [agreement]);
  }, [setValue, agreement]);

  const handleFileChange = (value, name) => setValue(name, value, true);

  const handleUploadStarted = (_, name) => clearError(name);

  return (
    <NumberedForm.Item title="Attachments">
      {fileCategories.map(category => {
        const name = `${FileKeyPrefix}${category.id}`;
        const key =
          agreement && category.id === FeeAgreementFileCategoryId ? agreement.id : category.id; // To force a re-render when agreement was selected from parent
        return (
          <FileUploader
            key={key}
            name={name}
            category={category}
            defaultEntityType={EntityType.Placement}
            endpoint={Endpoints.Files}
            errorText={errors[name]?.message}
            files={files[category.id]}
            mode="categoryField"
            onAttachmentsChanged={handleFileChange}
            onFileUploadStarted={handleUploadStarted}
            softDeleteForExistingItems={softDeleteForExistingItems}
            disabled={readOnly}
            fileExplorerSections={fileExplorerSections}
            multiple={category.multiple}
            maxNumberOfFiles={category.multiple ? maxNumberOfFiles : 1}
          />
        );
      })}
    </NumberedForm.Item>
  );
};

PlacementFilesSection.defaultProps = {
  readOnly: false,
  files: [],
  fileExplorerSections: [],
  /** Wheter to only remove a local file reference or completely remove the file from the server   */
  softDeleteForExistingItems: false,
  agreement: null
};

export default PlacementFilesSection;
